import { useCallback, useEffect, useState } from "react";
import { useLiveEvent } from "../lib/useLiveEvents.js";
import { LiveIndicator } from "../components/LiveIndicator.js";
import { Skeleton } from "../components/Skeleton.js";

interface Alert {
  id: number;
  cpId: string;
  connectorId: number | null;
  kind: string;
  severity: "info" | "warning" | "critical";
  message: string;
  createdAt: string;
  acknowledgedAt: string | null;
  acknowledgedBy: number | null;
}

function fmt(iso: string) {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function sevBadge(s: Alert["severity"]) {
  const map = {
    info: "nx-badge nx-badge--neutral",
    warning: "nx-badge nx-badge--charging",
    critical: "nx-badge nx-badge--fault",
  };
  return <span className={map[s]}>{s}</span>;
}

export function AlertsPage() {
  // null = ainda não carregou; [] = carregou e lista vazia (fica "tudo
  // tranquilo"). Diferenciar evita o piscar de "nenhum alerta" no primeiro
  // frame.
  const [alerts, setAlerts] = useState<Alert[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [onlyOpen, setOnlyOpen] = useState(true);

  const load = useCallback(() => {
    fetch("/api/alerts?onlyOpen=" + (onlyOpen ? "1" : "0"), {
      credentials: "include",
    })
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(String(r.status))),
      )
      .then((d: { alerts: Alert[] }) => setAlerts(d.alerts))
      .catch((e: Error) => setError(e.message));
  }, [onlyOpen]);

  // Load inicial. O polling foi removido — /ws/live avisa via
  // `alert.created` sempre que uma linha nova entra em `alerts`.
  useEffect(() => {
    load();
  }, [load]);

  // Push ao vivo: quando o backend emite `alert.created`, adiciona no topo
  // da lista imediatamente. O evento não carrega `acknowledgedAt` (é
  // sempre um alerta recém-nascido); mapeamos os campos do payload para
  // o shape usado na tabela.
  useLiveEvent("alert.created", (p) => {
    // Se filtrando "somente abertos", o alerta novo entra (é aberto).
    // Se filtrando "todos", também entra.
    setAlerts((prev) => {
      const base = prev ?? [];
      // Deduplica: pode chegar duplicado se o poller e um refresh
      // manual se cruzarem
      if (base.some((a) => a.id === p.id)) return base;
      const sev: Alert["severity"] =
        p.level === "error"
          ? "critical"
          : p.level === "warning"
            ? "warning"
            : "info";
      const fresh: Alert = {
        id: p.id,
        cpId: p.cpId ?? "",
        connectorId: null,
        kind: "live",
        severity: sev,
        message: p.message,
        createdAt: p.createdAt,
        acknowledgedAt: null,
        acknowledgedBy: null,
      };
      return [fresh, ...base];
    });
  });

  async function resolve(id: number) {
    // Path canônico: /resolve. Backend também aceita /ack como alias legado.
    // Ao resolver, o server cria automaticamente uma manutenção corretiva.
    await fetch(`/api/alerts/${id}/resolve`, {
      method: "POST",
      credentials: "include",
    });
    load();
  }

  // Contagem para o subtítulo. Enquanto null, mostra descritivo genérico —
  // não mentir número parcial: "0 ativos" agora seria falso, e o operador
  // usa esse número para decidir se atende ou volta ao café.
  const openCount = alerts?.filter((a) => !a.acknowledgedAt).length ?? null;
  const now = new Date();
  const startOfDay = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  const resolvedToday =
    alerts?.filter(
      (a) =>
        a.acknowledgedAt && new Date(a.acknowledgedAt).getTime() >= startOfDay,
    ).length ?? null;
  const sub =
    alerts == null
      ? "Carregando alertas…"
      : `${openCount} ${openCount === 1 ? "ativo" : "ativos"}, ${resolvedToday} resolvido${resolvedToday === 1 ? "" : "s"} hoje`;

  return (
    <div>
      <div className="nx-page-head">
        <div className="nx-page-head__title">
          <span className="nx-page-head__eyebrow">Monitoramento</span>
          <h1>Alertas</h1>
          <div className="nx-page-head__sub">{sub}</div>
        </div>
        <div className="nx-page-head__actions">
          <label style={{ fontSize: ".875rem", color: "var(--nx-mute)" }}>
            <input
              type="checkbox"
              checked={onlyOpen}
              onChange={(e) => setOnlyOpen(e.target.checked)}
            />{" "}
            somente não resolvidos
          </label>
          <LiveIndicator />
        </div>
      </div>

      {error && (
        <div className="nx-error" style={{ marginBottom: "var(--nx-space-4)" }}>
          {error}
        </div>
      )}

      <div className="nx-card">
        {alerts == null ? (
          <Skeleton variant="row" count={5} height={40} gap={6} />
        ) : alerts.length === 0 ? (
          <p
            style={{ color: "var(--nx-mute)", margin: 0, textAlign: "center" }}
          >
            {onlyOpen
              ? "Nenhum alerta em aberto. Tudo tranquilo."
              : "Nenhum alerta."}
          </p>
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Severidade</th>
                  <th>Carregador</th>
                  <th>Tipo</th>
                  <th>Mensagem</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((a) => (
                  <tr key={a.id}>
                    <td>{fmt(a.createdAt)}</td>
                    <td>{sevBadge(a.severity)}</td>
                    <td className="mono">
                      {a.cpId}
                      {a.connectorId ? `#${a.connectorId}` : ""}
                    </td>
                    <td className="mono">{a.kind}</td>
                    <td>{a.message}</td>
                    <td>
                      {a.acknowledgedAt ? (
                        <span className="nx-badge nx-badge--available">
                          resolvido
                        </span>
                      ) : (
                        <span className="nx-badge nx-badge--charging">
                          aberto
                        </span>
                      )}
                    </td>
                    <td>
                      {!a.acknowledgedAt && (
                        <button
                          type="button"
                          className="nx-btn nx-btn--sm"
                          onClick={() => void resolve(a.id)}
                        >
                          Resolvido
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
